import {ANALYSIS_OUTPUT_STEP_TYPES} from 'analysis-tool/AnalysisToolConstants';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {
  getIconForView,
  getTitleForView,
  isTopLevelAnalysisView,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {PipelineStepDataKind, PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL, PipelineStepRawData, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {
  getChildViewsForView,
  getOutputDataForView,
  isChartView,
  isDataFrameView,
} from 'pipelines/PipelineViewUtils';
import {pipelineStepRawDataIsOfKind} from 'pipelines/StepDataUtils';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {isSendToDocumentEnabled} from './OutputsUtil';
import {
  AnalysisOutputConfigWithoutResultsMapping,
  AnalysisOutputOptionItem,
  AnalysisOutputType,
  BaseAnalysisOutputConfig,
} from './Types';

function isResultOptionEnabled(): boolean {
  return isFeatureEnabled({feature: ResultsFeature.RESULTS_CREATE});
}

type GetOutputConfigFn<T extends AnalysisOutputType> = (
  baseOutputConfig: BaseAnalysisOutputConfig,
  view: PipelineViewGraphQL,
  rawData: PipelineStepRawData
) => Extract<AnalysisOutputConfigWithoutResultsMapping, {type: T}> | null;

/** Get AnalysisOutputType.DATASET output config, or null if not applicable to the view. */
const getDatasetOutputConfig: GetOutputConfigFn<AnalysisOutputType.DATASET> = (
  baseOutputConfig,
  view,
  rawData
) =>
  isDataFrameView(view) && pipelineStepRawDataIsOfKind(PipelineStepDataKind.DATASET, rawData)
    ? {...baseOutputConfig, type: AnalysisOutputType.DATASET, dataFrame: rawData}
    : null;

/** Get AnalysisOutputType.RESULT output config, or null if not applicable to the view. */
const getResultOutputConfig: GetOutputConfigFn<AnalysisOutputType.RESULT> = (
  baseOutputConfig,
  view,
  rawData
) =>
  isDataFrameView(view) &&
  pipelineStepRawDataIsOfKind(PipelineStepDataKind.DATASET, rawData) &&
  isResultOptionEnabled()
    ? {
        ...baseOutputConfig,
        type: AnalysisOutputType.RESULT,
        dataFrame: rawData,
        targetSpecificMappingState: null,
      }
    : null;

/** Get AnalysisOutputType.CHART output config, or null if not applicable to the view. */
const getChartOutputConfig: GetOutputConfigFn<AnalysisOutputType.CHART> = (baseOutputConfig, view, rawData) =>
  isChartView(view) &&
  pipelineStepRawDataIsOfKind(PipelineStepDataKind.CHART, rawData) &&
  isSendToDocumentEnabled()
    ? {
        ...baseOutputConfig,
        type: AnalysisOutputType.CHART,
        chart: rawData,
      }
    : null;

/** Get all output configs that are applicable to a view. */
function getOutputConfigsForView(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  icon: BenchlingIconName,
  parentViewTitle: string | null
): Array<AnalysisOutputConfigWithoutResultsMapping> {
  const step = view.lineage[view.lineage.length - 1].step;
  const rawData = getOutputDataForView(view);
  if (rawData == null) {
    return [];
  }

  const baseOutputConfig: BaseAnalysisOutputConfig = {
    icon,
    outputName: rawData.name,
    parentOutputName: parentViewTitle,
    step: getLegacyStepFromAnalysis(analysis, step.id),
  };

  return [getDatasetOutputConfig, getResultOutputConfig, getChartOutputConfig]
    .map((getOutputConfigFn) => getOutputConfigFn(baseOutputConfig, view, rawData))
    .filter((outputConfig) => outputConfig != null);
}

/** Get all output options for a view, and its child views. */
function getOutputOptionsForViewAndChildViews(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL,
  indentLevel: number = 0,
  parentViewTitle: string | null = null
): Array<Omit<AnalysisOutputOptionItem, 'selectorIndex'>> {
  const title = getTitleForView(view); // `sidebarItemData.title`
  const icon = getIconForView(analysis, view);
  const step = view.lineage[view.lineage.length - 1].step;
  const outputConfigs = getOutputConfigsForView(analysis, view, icon, parentViewTitle);

  // Do not output the outputs of outputs
  if (ANALYSIS_OUTPUT_STEP_TYPES.find((stepType) => stepType === step.stepType)) {
    return [];
  }

  const childOptionItems = getChildViewsForView(analysis, view)
    .filter((childView) => !isTopLevelAnalysisView(childView, analysis)) // Child views may themselves be top-level
    .flatMap((childView) =>
      getOutputOptionsForViewAndChildViews(analysis, childView, indentLevel + 1, title)
    );

  // Hide views that have neither config options nor a child with config options
  if (
    outputConfigs.length === 0 &&
    childOptionItems.length === 0 &&
    step.statusCode !== PipelineStepStatusCode.RUNNING
  ) {
    return [];
  }

  return [
    {
      indentLevel,
      sidebarItemData: {icon, title},
      outputConfigs,
      disabledReason:
        outputConfigs.length > 0
          ? null
          : step.statusCode === PipelineStepStatusCode.RUNNING
            ? 'This view is still running.'
            : 'This view cannot be output.',
    },
    ...childOptionItems,
  ];
}

/** Get all output options for an analysis, for use in `<AnalysisOutputSelector>`. */
export default function getViewBasedOutputOptions(
  analysis: PipelineGraphQL
): Array<AnalysisOutputOptionItem> {
  return analysis.views
    .filter((view) => isTopLevelAnalysisView(view, analysis))
    .flatMap((view) => getOutputOptionsForViewAndChildViews(analysis, view))
    .map((option, selectorIndex) => ({...option, selectorIndex}));
}
